import React, { createContext, useContext, useState, useRef, useEffect, ReactNode, useCallback } from 'react';
import { Article } from '../types';
import { extractYouTubeId, isYouTubeUrl, loadYouTubeIFrameAPI } from '../utils/youtube';

/**
 * Generates a valid 10-second PCM WAV silent audio Blob URL.
 * Unlike 46-byte zero-sample headers, this provides genuine audio frames
 * to keep mobile iOS Safari & Android Chrome background audio daemons active.
 */
function createSilentAudioBlobUrl(durationSeconds = 10): string {
  try {
    const sampleRate = 8000;
    const numChannels = 1;
    const bitsPerSample = 8;
    const numSamples = sampleRate * durationSeconds;
    const buffer = new ArrayBuffer(44 + numSamples);
    const view = new DataView(buffer);

    // RIFF chunk
    view.setUint32(0, 0x52494646, false); // "RIFF"
    view.setUint32(4, 36 + numSamples, true);
    view.setUint32(8, 0x57415645, false); // "WAVE"

    // fmt chunk
    view.setUint32(12, 0x666d7420, false); // "fmt "
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true); // Linear PCM
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numChannels * (bitsPerSample / 8), true);
    view.setUint16(32, numChannels * (bitsPerSample / 8), true);
    view.setUint16(34, bitsPerSample, true);

    // data chunk
    view.setUint32(36, 0x64617461, false); // "data"
    view.setUint32(40, numSamples, true);

    // 8-bit unsigned PCM silence is 128 (0x80)
    const bytes = new Uint8Array(buffer, 44, numSamples);
    bytes.fill(128);

    const blob = new Blob([buffer], { type: 'audio/wav' });
    return URL.createObjectURL(blob);
  } catch (_) {
    return 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAAAAA==';
  }
}

interface AudioPlayerContextType {
  currentArticle: Article | null;
  isPlaying: boolean;
  isLoading: boolean;
  currentTime: number;
  duration: number;
  progress: number;
  playbackRate: number;
  audioNotification: string | null;
  isPiPSupported: boolean;
  isPiPActive: boolean;
  togglePiP: () => Promise<void>;
  playArticle: (article: Article) => Promise<void>;
  pause: () => void;
  togglePlay: (article?: Article) => Promise<void>;
  seekTo: (timeInSeconds: number) => void;
  seekByPercent: (percent: number) => void;
  setRate: (rate: number) => void;
  toggleRate: () => void;
  clearNotification: () => void;
  stop: () => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(undefined);

export const AudioPlayerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentArticle, setCurrentArticle] = useState<Article | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [audioNotification, setAudioNotification] = useState<string | null>(null);
  const [isPiPActive, setIsPiPActive] = useState(false);
  const [isPiPSupported, setIsPiPSupported] = useState(false);

  // Persistent media element refs
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const carrierAudioRef = useRef<HTMLAudioElement | null>(null);
  const webAudioCtxRef = useRef<any>(null);
  const webAudioSourceRef = useRef<any>(null);
  const ytPlayerRef = useRef<any>(null);
  const ytPlayerReadyRef = useRef(false);
  const ytTimeIntervalRef = useRef<any>(null);
  const currentUrlRef = useRef<string | null>(null);
  const isYouTubeRef = useRef<boolean>(false);
  const isPlayingRef = useRef<boolean>(false);
  const wasPlayingBeforeHiddenRef = useRef<boolean>(false);
  const currentTimeRef = useRef<number>(0);
  const durationRef = useRef<number>(0);
  const playbackRateRef = useRef<number>(1);
  const wakeLockRef = useRef<any>(null);
  const resumeRetryTimeoutRef = useRef<any>(null);
  const pipWindowRef = useRef<any>(null);
  const pipVideoRef = useRef<HTMLVideoElement | null>(null);

  const progress = duration > 0 ? Math.min(1, Math.max(0, currentTime / duration)) : 0;

  const clearNotification = useCallback(() => {
    setAudioNotification(null);
  }, []);

  // Check Picture-in-Picture capability on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hasDocPiP = 'documentPictureInPicture' in window;
      const hasVideoPiP = typeof document !== 'undefined' && 'pictureInPictureEnabled' in document;
      setIsPiPSupported(Boolean(hasDocPiP || hasVideoPiP));
    }
  }, []);

  // Safe Screen WakeLock to avoid screen dimming during intentional listening
  const requestWakeLock = useCallback(async () => {
    try {
      if ('wakeLock' in navigator && (navigator as any).wakeLock?.request) {
        if (!wakeLockRef.current) {
          wakeLockRef.current = await (navigator as any).wakeLock.request('screen');
          wakeLockRef.current.addEventListener('release', () => {
            wakeLockRef.current = null;
          });
        }
      }
    } catch (_) {}
  }, []);

  const releaseWakeLock = useCallback(async () => {
    try {
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    } catch (_) {}
  }, []);

  // Web Audio Hardware Keepalive session
  const initWebAudioSession = useCallback(() => {
    try {
      const AudioCtxClass = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtxClass) return;

      if (!webAudioCtxRef.current) {
        const ctx = new AudioCtxClass();
        webAudioCtxRef.current = ctx;

        // Create a 1-second buffer of silence
        const buffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.loop = true;

        // Non-zero tiny gain (0.0001) keeps browser audio pipeline active in hardware audio session
        const gain = ctx.createGain();
        gain.gain.value = 0.0001;
        source.connect(gain);
        gain.connect(ctx.destination);
        source.start(0);
        webAudioSourceRef.current = source;
      }

      if (webAudioCtxRef.current.state === 'suspended') {
        webAudioCtxRef.current.resume().catch(() => {});
      }
    } catch (err) {
      console.warn('[AUDIO KEEPALIVE] WebAudio session init:', err);
    }
  }, []);

  // Update Media Session Position & State
  const updateMediaSessionPlaybackState = useCallback((state: 'playing' | 'paused' | 'none') => {
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = state;
    }
  }, []);

  const updateMediaSessionPosition = useCallback((pos: number, dur: number, rate: number) => {
    if ('mediaSession' in navigator) {
      try {
        if (dur > 0 && isFinite(dur) && isFinite(pos) && pos >= 0) {
          navigator.mediaSession.setPositionState({
            duration: Math.max(dur, 1),
            playbackRate: rate || 1,
            position: Math.min(pos, dur)
          });
        }
      } catch (_) {}
    }
  }, []);

  // Synchronize refs with state
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    currentTimeRef.current = currentTime;
  }, [currentTime]);

  useEffect(() => {
    durationRef.current = duration;
  }, [duration]);

  useEffect(() => {
    playbackRateRef.current = playbackRate;
  }, [playbackRate]);

  // Setup / Register MediaSession API metadata and action handlers
  const setupMediaSession = useCallback((article: Article) => {
    if (!('mediaSession' in navigator)) return;

    const artworkUrl = article.imageUrl || 'https://i.postimg.cc/vTfzmk02/Iara-perfil-2.jpg';
    const iaraAvatar = 'https://i.postimg.cc/vTfzmk02/Iara-perfil-2.jpg';

    navigator.mediaSession.metadata = new MediaMetadata({
      title: article.title,
      artist: 'Narração da Iara • Literafan',
      album: 'Literafan Magazine',
      artwork: [
        { src: iaraAvatar, sizes: '96x96', type: 'image/jpeg' },
        { src: iaraAvatar, sizes: '128x128', type: 'image/jpeg' },
        { src: iaraAvatar, sizes: '192x192', type: 'image/jpeg' },
        { src: artworkUrl, sizes: '256x256', type: 'image/jpeg' },
        { src: artworkUrl, sizes: '384x384', type: 'image/jpeg' },
        { src: artworkUrl, sizes: '512x512', type: 'image/jpeg' }
      ]
    });

    // Action handlers referencing refs to avoid stale closures
    navigator.mediaSession.setActionHandler('play', async () => {
      try {
        initWebAudioSession();
        if (carrierAudioRef.current) {
          carrierAudioRef.current.play().catch(() => {});
        }

        if (isYouTubeRef.current && ytPlayerRef.current && ytPlayerReadyRef.current) {
          ytPlayerRef.current.playVideo();
          setIsPlaying(true);
          isPlayingRef.current = true;
          updateMediaSessionPlaybackState('playing');
          requestWakeLock();
        } else if (audioElementRef.current && !isYouTubeRef.current) {
          await audioElementRef.current.play();
          setIsPlaying(true);
          isPlayingRef.current = true;
          updateMediaSessionPlaybackState('playing');
          requestWakeLock();
        }
      } catch (err) {
        console.warn('MediaSession play handler error:', err);
      }
    });

    navigator.mediaSession.setActionHandler('pause', () => {
      wasPlayingBeforeHiddenRef.current = false;
      if (audioElementRef.current && !isYouTubeRef.current) {
        audioElementRef.current.pause();
      }
      if (carrierAudioRef.current) {
        carrierAudioRef.current.pause();
      }
      if (ytPlayerRef.current && ytPlayerReadyRef.current) {
        try {
          ytPlayerRef.current.pauseVideo();
        } catch (_) {}
      }
      setIsPlaying(false);
      isPlayingRef.current = false;
      updateMediaSessionPlaybackState('paused');
      releaseWakeLock();
    });

    navigator.mediaSession.setActionHandler('stop', () => {
      stop();
    });

    navigator.mediaSession.setActionHandler('seekto', (details) => {
      if (details.seekTime !== undefined) {
        seekTo(details.seekTime);
      }
    });

    navigator.mediaSession.setActionHandler('seekforward', (details) => {
      const offset = details.seekOffset || 10;
      seekTo(currentTimeRef.current + offset);
    });

    navigator.mediaSession.setActionHandler('seekbackward', (details) => {
      const offset = details.seekOffset || 10;
      seekTo(Math.max(0, currentTimeRef.current - offset));
    });

    navigator.mediaSession.setActionHandler('previoustrack', () => {
      seekTo(0);
    });

    navigator.mediaSession.setActionHandler('nexttrack', () => {
      seekTo(currentTimeRef.current + 30);
    });
  }, [updateMediaSessionPlaybackState, requestWakeLock, releaseWakeLock, initWebAudioSession]);

  // Clean up YouTube player
  const destroyYtPlayer = useCallback(() => {
    if (ytTimeIntervalRef.current) {
      clearInterval(ytTimeIntervalRef.current);
      ytTimeIntervalRef.current = null;
    }
    if (ytPlayerRef.current) {
      try {
        ytPlayerRef.current.destroy();
      } catch (_) {}
      ytPlayerRef.current = null;
      ytPlayerReadyRef.current = false;
    }
  }, []);

  // Initialize DOM-attached persistent HTML5 audio and background audio carrier
  useEffect(() => {
    if (typeof document === 'undefined') return;

    // 1. Native Primary Audio Element (for Supabase MP3 and direct audio files)
    let audio = document.getElementById('literafan-native-audio') as HTMLAudioElement;
    if (!audio) {
      audio = document.createElement('audio');
      audio.id = 'literafan-native-audio';
      audio.preload = 'auto';
      audio.setAttribute('playsinline', 'true');
      audio.setAttribute('webkit-playsinline', 'true');
      // Visually clean, not 0.001 opacity / -9999 z-index to prevent mobile browser sleep throttling
      audio.style.position = 'fixed';
      audio.style.bottom = '-100px';
      audio.style.left = '-100px';
      audio.style.width = '10px';
      audio.style.height = '10px';
      audio.style.pointerEvents = 'none';
      document.body.appendChild(audio);
    }
    audioElementRef.current = audio;

    // 2. Continuous 10s Silent PCM WAV Carrier Audio Element
    let carrier = document.getElementById('literafan-audio-carrier') as HTMLAudioElement;
    if (!carrier) {
      carrier = document.createElement('audio');
      carrier.id = 'literafan-audio-carrier';
      carrier.preload = 'auto';
      carrier.setAttribute('playsinline', 'true');
      carrier.setAttribute('webkit-playsinline', 'true');
      carrier.loop = true;
      carrier.volume = 0.02;
      carrier.src = createSilentAudioBlobUrl(10);
      carrier.style.position = 'fixed';
      carrier.style.bottom = '-100px';
      carrier.style.left = '-100px';
      carrier.style.width = '10px';
      carrier.style.height = '10px';
      carrier.style.pointerEvents = 'none';
      document.body.appendChild(carrier);
    }
    carrierAudioRef.current = carrier;

    // Direct audio event handlers
    const onTimeUpdate = () => {
      if (audioElementRef.current && !isYouTubeRef.current) {
        const curr = audioElementRef.current.currentTime || 0;
        const dur = audioElementRef.current.duration || 0;
        setCurrentTime(curr);
        currentTimeRef.current = curr;
        if (dur > 0 && !isNaN(dur) && isFinite(dur)) {
          setDuration(dur);
          durationRef.current = dur;
          updateMediaSessionPosition(curr, dur, playbackRateRef.current);
        }
      }
    };

    const onLoadedMetadata = () => {
      if (audioElementRef.current && !isYouTubeRef.current) {
        const dur = audioElementRef.current.duration || 0;
        if (dur > 0 && !isNaN(dur) && isFinite(dur)) {
          setDuration(dur);
          durationRef.current = dur;
        }
      }
    };

    const onPlay = () => {
      if (!isYouTubeRef.current) {
        setIsPlaying(true);
        isPlayingRef.current = true;
        setIsLoading(false);
        updateMediaSessionPlaybackState('playing');
        requestWakeLock();
      }
    };

    const onPause = () => {
      if (!isYouTubeRef.current) {
        // If paused involuntarily by mobile OS screen sleep, preserve state to allow instant resume
        if (document.visibilityState === 'hidden' && wasPlayingBeforeHiddenRef.current) {
          updateMediaSessionPlaybackState('paused');
        } else {
          setIsPlaying(false);
          isPlayingRef.current = false;
          setIsLoading(false);
          updateMediaSessionPlaybackState('paused');
          releaseWakeLock();
        }
      }
    };

    const onEnded = () => {
      if (!isYouTubeRef.current) {
        setIsPlaying(false);
        isPlayingRef.current = false;
        setIsLoading(false);
        setCurrentTime(0);
        updateMediaSessionPlaybackState('none');
        releaseWakeLock();
      }
    };

    const onError = (e: any) => {
      if (!isYouTubeRef.current && currentUrlRef.current) {
        console.warn('Audio element error:', e);
        setIsLoading(false);
        setIsPlaying(false);
        isPlayingRef.current = false;
        setAudioNotification('Áudio não disponível no momento. Tente novamente mais tarde.');
        releaseWakeLock();
      }
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('durationchange', onLoadedMetadata);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('durationchange', onLoadedMetadata);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
    };
  }, [updateMediaSessionPlaybackState, updateMediaSessionPosition, requestWakeLock, releaseWakeLock]);

  // Handle visibility changes (lock screen, switching tabs, leaving browser)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Record whether we were actively playing when user exited/locked
        wasPlayingBeforeHiddenRef.current = isPlayingRef.current;

        // Keep hardware audio session running via carrier
        if (isPlayingRef.current) {
          if (carrierAudioRef.current && carrierAudioRef.current.paused) {
            carrierAudioRef.current.play().catch(() => {});
          }
          if (webAudioCtxRef.current && webAudioCtxRef.current.state === 'suspended') {
            webAudioCtxRef.current.resume().catch(() => {});
          }
        }
      } else if (document.visibilityState === 'visible') {
        // User came back to the app or unlocked the phone
        if (wasPlayingBeforeHiddenRef.current) {
          requestWakeLock();

          // If playback was involuntarily suspended by mobile OS backgrounding, smoothly resume
          if (!isPlayingRef.current) {
            if (isYouTubeRef.current && ytPlayerRef.current && ytPlayerReadyRef.current) {
              try {
                if (carrierAudioRef.current) {
                  carrierAudioRef.current.play().catch(() => {});
                }
                ytPlayerRef.current.playVideo();
                setIsPlaying(true);
                isPlayingRef.current = true;
                updateMediaSessionPlaybackState('playing');
              } catch (_) {}
            } else if (audioElementRef.current && audioElementRef.current.src && !isYouTubeRef.current) {
              audioElementRef.current.play().then(() => {
                setIsPlaying(true);
                isPlayingRef.current = true;
                updateMediaSessionPlaybackState('playing');
              }).catch(() => {});
            }
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [requestWakeLock, updateMediaSessionPlaybackState]);

  // Start background tracking for YouTube player
  const startYtTimeTracking = useCallback((player: any) => {
    if (ytTimeIntervalRef.current) clearInterval(ytTimeIntervalRef.current);
    ytTimeIntervalRef.current = setInterval(() => {
      try {
        if (player && typeof player.getCurrentTime === 'function') {
          const curr = player.getCurrentTime() || 0;
          const dur = player.getDuration() || 0;
          setCurrentTime(curr);
          currentTimeRef.current = curr;
          if (dur > 0) {
            setDuration(dur);
            durationRef.current = dur;
            updateMediaSessionPosition(curr, dur, playbackRateRef.current);
          }
        }
      } catch (_) {}
    }, 250);
  }, [updateMediaSessionPosition]);

  // Main play function
  const playArticle = useCallback(async (article: Article) => {
    const rawUrl = article?.audioUrl?.trim();
    if (!rawUrl || rawUrl === '' || rawUrl === '#' || rawUrl === 'undefined' || rawUrl === 'null') {
      setAudioNotification('Áudio de narração não disponível para este artigo.');
      return;
    }

    setCurrentArticle(article);
    setupMediaSession(article);
    setIsLoading(true);

    // Activate WebAudio and continuous silent carrier in user gesture
    initWebAudioSession();
    if (carrierAudioRef.current) {
      carrierAudioRef.current.play().catch(() => {});
    }

    const isYT = isYouTubeUrl(rawUrl);
    const ytId = extractYouTubeId(rawUrl);

    // ==========================================
    // 1. YouTube Audio
    // ==========================================
    if (isYT && ytId) {
      isYouTubeRef.current = true;

      // Stop native audio if it was playing
      if (audioElementRef.current) {
        audioElementRef.current.pause();
        audioElementRef.current.src = '';
      }

      // Re-use current YouTube instance if same video
      if (currentUrlRef.current === rawUrl && ytPlayerRef.current && ytPlayerReadyRef.current) {
        try {
          ytPlayerRef.current.playVideo();
          setIsPlaying(true);
          isPlayingRef.current = true;
          setIsLoading(false);
          startYtTimeTracking(ytPlayerRef.current);
          updateMediaSessionPlaybackState('playing');
          requestWakeLock();
          return;
        } catch (e) {
          destroyYtPlayer();
        }
      }

      destroyYtPlayer();
      currentUrlRef.current = rawUrl;

      try {
        await loadYouTubeIFrameAPI();

        // Ensure container is rendered in layout (not 0.001 opacity which triggers mobile pause heuristics)
        let container = document.getElementById('global-yt-audio-container');
        if (!container) {
          container = document.createElement('div');
          container.id = 'global-yt-audio-container';
          container.style.position = 'fixed';
          container.style.bottom = '12px';
          container.style.left = '12px';
          container.style.width = '1px';
          container.style.height = '1px';
          container.style.overflow = 'hidden';
          container.style.pointerEvents = 'none';
          container.style.zIndex = '1';
          document.body.appendChild(container);
        }

        container.innerHTML = '<div id="global-yt-audio-player"></div>';

        new (window as any).YT.Player('global-yt-audio-player', {
          videoId: ytId,
          playerVars: {
            playsinline: 1,
            controls: 0,
            disablekb: 1,
            fs: 0,
            modestbranding: 1,
            rel: 0,
            iv_load_policy: 3,
            origin: window.location.origin
          },
          events: {
            onReady: (event: any) => {
              ytPlayerRef.current = event.target;
              ytPlayerReadyRef.current = true;
              event.target.setPlaybackRate(playbackRateRef.current);
              event.target.playVideo();
              setIsPlaying(true);
              isPlayingRef.current = true;
              setIsLoading(false);
              const dur = event.target.getDuration() || 0;
              if (dur > 0) {
                setDuration(dur);
                durationRef.current = dur;
              }
              startYtTimeTracking(event.target);
              updateMediaSessionPlaybackState('playing');
              requestWakeLock();
            },
            onStateChange: (event: any) => {
              const state = event.data;
              const dur = event.target.getDuration() || 0;
              if (dur > 0) {
                setDuration(dur);
                durationRef.current = dur;
              }

              if (state === 1) { // PLAYING
                setIsPlaying(true);
                isPlayingRef.current = true;
                setIsLoading(false);
                startYtTimeTracking(event.target);
                if (carrierAudioRef.current && carrierAudioRef.current.paused) {
                  carrierAudioRef.current.play().catch(() => {});
                }
                updateMediaSessionPlaybackState('playing');
                requestWakeLock();
              } else if (state === 2) { // PAUSED
                // If paused involuntarily while screen was locked or tab was backgrounded:
                if (document.visibilityState === 'hidden' && wasPlayingBeforeHiddenRef.current) {
                  // Keep media session state and carrier active so lock screen widget remains operational
                  updateMediaSessionPlaybackState('playing');
                  if (carrierAudioRef.current && carrierAudioRef.current.paused) {
                    carrierAudioRef.current.play().catch(() => {});
                  }
                  // Softly schedule resume attempt
                  if (resumeRetryTimeoutRef.current) clearTimeout(resumeRetryTimeoutRef.current);
                  resumeRetryTimeoutRef.current = setTimeout(() => {
                    if (wasPlayingBeforeHiddenRef.current && ytPlayerRef.current) {
                      try {
                        ytPlayerRef.current.playVideo();
                      } catch (_) {}
                    }
                  }, 300);
                } else {
                  setIsPlaying(false);
                  isPlayingRef.current = false;
                  setIsLoading(false);
                  if (carrierAudioRef.current) {
                    carrierAudioRef.current.pause();
                  }
                  updateMediaSessionPlaybackState('paused');
                  releaseWakeLock();
                }
              } else if (state === 0) { // ENDED
                setIsPlaying(false);
                isPlayingRef.current = false;
                setIsLoading(false);
                setCurrentTime(0);
                currentTimeRef.current = 0;
                if (carrierAudioRef.current) {
                  carrierAudioRef.current.pause();
                }
                updateMediaSessionPlaybackState('none');
                releaseWakeLock();
              } else if (state === 3) { // BUFFERING
                setIsLoading(true);
              }
            },
            onError: (err: any) => {
              console.warn('YouTube Audio playback error:', err);
              setIsPlaying(false);
              isPlayingRef.current = false;
              setIsLoading(false);
              if (carrierAudioRef.current) {
                carrierAudioRef.current.pause();
              }
              setAudioNotification('Áudio de narração indisponível no momento.');
              releaseWakeLock();
            }
          }
        });
      } catch (err) {
        console.warn('Error starting YouTube audio:', err);
        setIsLoading(false);
        setIsPlaying(false);
        isPlayingRef.current = false;
        setAudioNotification('Erro ao carregar narração.');
      }
      return;
    }

    // ==========================================
    // 2. Direct HTML5 Audio (MP3 / WAV / Supabase Storage)
    // ==========================================
    isYouTubeRef.current = false;
    destroyYtPlayer();

    let audio = audioElementRef.current;
    if (!audio) {
      audio = document.createElement('audio');
      audio.id = 'literafan-native-audio';
      audio.preload = 'auto';
      audio.setAttribute('playsinline', 'true');
      audio.setAttribute('webkit-playsinline', 'true');
      audio.style.position = 'fixed';
      audio.style.bottom = '-100px';
      audio.style.left = '-100px';
      audio.style.width = '10px';
      audio.style.height = '10px';
      audio.style.pointerEvents = 'none';
      document.body.appendChild(audio);
      audioElementRef.current = audio;
    }

    if (currentUrlRef.current === rawUrl && audio.src) {
      try {
        audio.playbackRate = playbackRateRef.current;
        await audio.play();
        setIsPlaying(true);
        isPlayingRef.current = true;
        setIsLoading(false);
        updateMediaSessionPlaybackState('playing');
        requestWakeLock();
        return;
      } catch (e) {
        console.warn('Could not resume audio, reloading source:', e);
      }
    }

    currentUrlRef.current = rawUrl;
    audio.pause();
    audio.src = rawUrl;
    audio.playbackRate = playbackRateRef.current;

    try {
      await audio.play();
      setIsPlaying(true);
      isPlayingRef.current = true;
      updateMediaSessionPlaybackState('playing');
      requestWakeLock();
    } catch (e: any) {
      console.warn('Direct audio playback failed:', e);
      setIsPlaying(false);
      isPlayingRef.current = false;
      setAudioNotification('Não foi possível reproduzir este áudio. Verifique sua conexão.');
      releaseWakeLock();
    } finally {
      setIsLoading(false);
    }
  }, [setupMediaSession, startYtTimeTracking, destroyYtPlayer, updateMediaSessionPlaybackState, requestWakeLock, releaseWakeLock, initWebAudioSession]);

  const pause = useCallback(() => {
    wasPlayingBeforeHiddenRef.current = false;
    if (resumeRetryTimeoutRef.current) clearTimeout(resumeRetryTimeoutRef.current);

    if (audioElementRef.current && !isYouTubeRef.current) {
      audioElementRef.current.pause();
    }
    if (carrierAudioRef.current) {
      carrierAudioRef.current.pause();
    }
    if (ytPlayerRef.current && ytPlayerReadyRef.current) {
      try {
        ytPlayerRef.current.pauseVideo();
      } catch (_) {}
    }
    setIsPlaying(false);
    isPlayingRef.current = false;
    updateMediaSessionPlaybackState('paused');
    releaseWakeLock();
  }, [updateMediaSessionPlaybackState, releaseWakeLock]);

  const togglePlay = useCallback(async (article?: Article) => {
    const targetArticle = article || currentArticle;
    if (!targetArticle) return;

    if (currentArticle?.id === targetArticle.id && isPlaying) {
      pause();
    } else {
      await playArticle(targetArticle);
    }
  }, [currentArticle, isPlaying, pause, playArticle]);

  const seekTo = useCallback((timeInSeconds: number) => {
    const dur = durationRef.current;
    const safeTime = Math.max(0, Math.min(timeInSeconds, dur > 0 ? dur : timeInSeconds));
    setCurrentTime(safeTime);
    currentTimeRef.current = safeTime;

    if (audioElementRef.current && !isYouTubeRef.current) {
      audioElementRef.current.currentTime = safeTime;
    }
    if (ytPlayerRef.current && ytPlayerReadyRef.current && isYouTubeRef.current) {
      try {
        ytPlayerRef.current.seekTo(safeTime, true);
      } catch (_) {}
    }

    updateMediaSessionPosition(safeTime, dur, playbackRateRef.current);
  }, [updateMediaSessionPosition]);

  const seekByPercent = useCallback((percent: number) => {
    const dur = durationRef.current;
    const targetTime = (percent / 100) * (dur || 1);
    seekTo(targetTime);
  }, [seekTo]);

  const setRate = useCallback((rate: number) => {
    setPlaybackRate(rate);
    playbackRateRef.current = rate;
    if (audioElementRef.current && !isYouTubeRef.current) {
      audioElementRef.current.playbackRate = rate;
    }
    if (ytPlayerRef.current && ytPlayerReadyRef.current && isYouTubeRef.current) {
      try {
        ytPlayerRef.current.setPlaybackRate(rate);
      } catch (_) {}
    }
    updateMediaSessionPosition(currentTimeRef.current, durationRef.current, rate);
  }, [updateMediaSessionPosition]);

  const toggleRate = useCallback(() => {
    const rates = [1, 1.25, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRateRef.current);
    const nextRate = rates[(currentIndex + 1) % rates.length];
    setRate(nextRate);
  }, [setRate]);

  const stop = useCallback(() => {
    wasPlayingBeforeHiddenRef.current = false;
    if (resumeRetryTimeoutRef.current) clearTimeout(resumeRetryTimeoutRef.current);

    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current.src = '';
    }
    if (carrierAudioRef.current) {
      carrierAudioRef.current.pause();
    }
    destroyYtPlayer();
    setIsPlaying(false);
    isPlayingRef.current = false;
    setIsLoading(false);
    setCurrentArticle(null);
    currentUrlRef.current = null;
    setCurrentTime(0);
    currentTimeRef.current = 0;
    setDuration(0);
    durationRef.current = 0;
    updateMediaSessionPlaybackState('none');
    releaseWakeLock();

    // Close PiP if open
    if (pipWindowRef.current) {
      try {
        pipWindowRef.current.close();
      } catch (_) {}
      pipWindowRef.current = null;
    }
    if (document.pictureInPictureElement) {
      try {
        document.exitPictureInPicture().catch(() => {});
      } catch (_) {}
    }
    setIsPiPActive(false);
  }, [destroyYtPlayer, updateMediaSessionPlaybackState, releaseWakeLock]);

  // Picture-in-Picture Toggle (Document PiP or Canvas/Video PiP)
  const togglePiP = useCallback(async () => {
    if (!currentArticle) return;

    // 1. If PiP already active, close it
    if (isPiPActive) {
      if (pipWindowRef.current) {
        try {
          pipWindowRef.current.close();
        } catch (_) {}
        pipWindowRef.current = null;
      }
      if (document.pictureInPictureElement) {
        try {
          await document.exitPictureInPicture();
        } catch (_) {}
      }
      setIsPiPActive(false);
      return;
    }

    // 2. Try Document Picture-in-Picture (Chrome 116+, Edge, Android Chrome 120+)
    if ('documentPictureInPicture' in window && (window as any).documentPictureInPicture?.requestWindow) {
      try {
        const pipWin = await (window as any).documentPictureInPicture.requestWindow({
          width: 380,
          height: 190
        });

        // Copy styles
        Array.from(document.styleSheets).forEach((sheet: any) => {
          try {
            if (sheet.href) {
              const link = document.createElement('link');
              link.rel = 'stylesheet';
              link.href = sheet.href;
              pipWin.document.head.appendChild(link);
            } else if (sheet.cssRules) {
              const style = document.createElement('style');
              style.textContent = Array.from(sheet.cssRules).map((r: any) => r.cssText).join('');
              pipWin.document.head.appendChild(style);
            }
          } catch (_) {}
        });

        const pipBody = pipWin.document.body;
        pipBody.style.margin = '0';
        pipBody.style.backgroundColor = '#121212';
        pipBody.style.color = '#f5f5f4';
        pipBody.style.fontFamily = 'system-ui, -apple-system, sans-serif';
        pipBody.style.display = 'flex';
        pipBody.style.alignItems = 'center';
        pipBody.style.justifyContent = 'center';
        pipBody.style.height = '100vh';
        pipBody.style.padding = '12px';
        pipBody.style.boxSizing = 'border-box';

        const updatePiPView = () => {
          const formatMinSec = (s: number) => {
            const m = Math.floor(s / 60);
            const sec = Math.floor(s % 60);
            return `${m}:${sec.toString().padStart(2, '0')}`;
          };

          pipBody.innerHTML = `
            <div style="width: 100%; display: flex; flex-direction: column; gap: 10px;">
              <div style="display: flex; align-items: center; gap: 12px;">
                <img src="https://i.postimg.cc/vTfzmk02/Iara-perfil-2.jpg" style="width: 44px; height: 44px; border-radius: 50%; border: 2px solid #ef4444; object-fit: cover;" />
                <div style="flex: 1; min-width: 0;">
                  <div style="font-size: 10px; font-weight: bold; text-transform: uppercase; color: #ef4444; letter-spacing: 0.5px;">Narração da Iara • Literafan</div>
                  <div style="font-size: 13px; font-weight: bold; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: #ffffff;">${currentArticle.title}</div>
                </div>
              </div>
              <div style="display: flex; align-items: center; justify-content: space-between; gap: 8px;">
                <span style="font-size: 11px; color: #a8a29e; font-variant-numeric: tabular-nums;">${formatMinSec(currentTimeRef.current)}</span>
                <div style="flex: 1; height: 4px; background: #292524; border-radius: 2px; overflow: hidden;">
                  <div style="width: ${(durationRef.current > 0 ? (currentTimeRef.current / durationRef.current) * 100 : 0)}%; height: 100%; background: #ef4444;"></div>
                </div>
                <span style="font-size: 11px; color: #a8a29e; font-variant-numeric: tabular-nums;">${formatMinSec(durationRef.current)}</span>
              </div>
              <div style="display: flex; align-items: center; justify-content: center; gap: 12px; margin-top: 2px;">
                <button id="pip-seek-back" style="background: #292524; border: none; color: #d6d3d1; padding: 6px 12px; border-radius: 9999px; font-size: 11px; cursor: pointer; font-weight: bold;">-10s</button>
                <button id="pip-toggle" style="background: #ef4444; border: none; color: #ffffff; padding: 8px 20px; border-radius: 9999px; font-size: 12px; cursor: pointer; font-weight: bold;">${isPlayingRef.current ? 'Pausar' : 'Ouvir'}</button>
                <button id="pip-seek-forward" style="background: #292524; border: none; color: #d6d3d1; padding: 6px 12px; border-radius: 9999px; font-size: 11px; cursor: pointer; font-weight: bold;">+10s</button>
              </div>
            </div>
          `;

          const toggleBtn = pipBody.querySelector('#pip-toggle');
          if (toggleBtn) {
            toggleBtn.addEventListener('click', () => {
              togglePlay();
              setTimeout(updatePiPView, 50);
            });
          }
          const backBtn = pipBody.querySelector('#pip-seek-back');
          if (backBtn) {
            backBtn.addEventListener('click', () => {
              seekTo(Math.max(0, currentTimeRef.current - 10));
              setTimeout(updatePiPView, 50);
            });
          }
          const fwdBtn = pipBody.querySelector('#pip-seek-forward');
          if (fwdBtn) {
            fwdBtn.addEventListener('click', () => {
              seekTo(currentTimeRef.current + 10);
              setTimeout(updatePiPView, 50);
            });
          }
        };

        updatePiPView();
        const pipInterval = setInterval(updatePiPView, 1000);

        pipWin.addEventListener('pagehide', () => {
          clearInterval(pipInterval);
          pipWindowRef.current = null;
          setIsPiPActive(false);
        });

        pipWindowRef.current = pipWin;
        setIsPiPActive(true);
        return;
      } catch (docPipErr) {
        console.warn('Document PiP request failed:', docPipErr);
      }
    }

    // 3. Fallback Video Canvas PiP (Safari, iOS, and general Android)
    try {
      let pipVideo = pipVideoRef.current;
      if (!pipVideo) {
        pipVideo = document.createElement('video');
        pipVideo.id = 'literafan-pip-video';
        pipVideo.muted = true;
        pipVideo.playsInline = true;
        pipVideo.setAttribute('playsinline', 'true');
        pipVideo.setAttribute('webkit-playsinline', 'true');
        pipVideo.style.position = 'fixed';
        pipVideo.style.bottom = '-200px';
        pipVideo.style.left = '-200px';
        pipVideo.style.width = '100px';
        pipVideo.style.height = '100px';
        document.body.appendChild(pipVideo);
        pipVideoRef.current = pipVideo;
      }

      const canvas = document.createElement('canvas');
      canvas.width = 480;
      canvas.height = 270;
      const ctx = canvas.getContext('2d');

      const avatar = new Image();
      avatar.crossOrigin = 'anonymous';
      avatar.src = 'https://i.postimg.cc/vTfzmk02/Iara-perfil-2.jpg';

      const drawFrame = () => {
        if (!ctx) return;
        ctx.fillStyle = '#0c0a09';
        ctx.fillRect(0, 0, 480, 270);

        try {
          if (avatar.complete) {
            ctx.drawImage(avatar, 40, 55, 160, 160);
          }
        } catch (_) {}

        ctx.fillStyle = '#ef4444';
        ctx.font = 'bold 16px sans-serif';
        ctx.fillText('NARRAÇÃO DA IARA', 220, 100);

        ctx.fillStyle = '#f5f5f4';
        ctx.font = 'bold 20px serif';
        const title = currentArticle.title || 'Artigo Literafan';
        ctx.fillText(title.slice(0, 24) + (title.length > 24 ? '...' : ''), 220, 135);

        ctx.fillStyle = '#a8a29e';
        ctx.font = '14px sans-serif';
        ctx.fillText('Literafan Magazine', 220, 165);
      };

      drawFrame();
      const stream = canvas.captureStream ? canvas.captureStream(10) : null;
      if (stream) {
        pipVideo.srcObject = stream;
        await pipVideo.play();
        if (pipVideo.requestPictureInPicture) {
          await pipVideo.requestPictureInPicture();
          setIsPiPActive(true);

          pipVideo.addEventListener('leavepictureinpicture', () => {
            setIsPiPActive(false);
          }, { once: true });
        }
      }
    } catch (vidPipErr) {
      console.warn('Video PiP failed:', vidPipErr);
      setAudioNotification('Modo flutuante não suportado neste navegador. Use os controles da tela de bloqueio.');
    }
  }, [currentArticle, isPiPActive, togglePlay, seekTo]);

  // Clean up on component unload
  useEffect(() => {
    return () => {
      if (resumeRetryTimeoutRef.current) clearTimeout(resumeRetryTimeoutRef.current);
      if (audioElementRef.current) {
        audioElementRef.current.pause();
        audioElementRef.current.src = '';
      }
      if (carrierAudioRef.current) {
        carrierAudioRef.current.pause();
      }
      if (pipWindowRef.current) {
        try {
          pipWindowRef.current.close();
        } catch (_) {}
      }
      destroyYtPlayer();
      releaseWakeLock();
    };
  }, [destroyYtPlayer, releaseWakeLock]);

  return (
    <AudioPlayerContext.Provider
      value={{
        currentArticle,
        isPlaying,
        isLoading,
        currentTime,
        duration,
        progress,
        playbackRate,
        audioNotification,
        isPiPSupported,
        isPiPActive,
        togglePiP,
        playArticle,
        pause,
        togglePlay,
        seekTo,
        seekByPercent,
        setRate,
        toggleRate,
        clearNotification,
        stop
      }}
    >
      {children}
    </AudioPlayerContext.Provider>
  );
};

export const useAudioPlayer = (): AudioPlayerContextType => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
  }
  return context;
};
